import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Button, Card, Checkbox, FilterSelect, type FilterOption } from '@wundo/ui';
import { UserPicker } from '@/components/pickers/UserPicker.tsx';
import type { OutreachQueueKind } from '@/lib/outreachQueue.ts';
import { listAllSkills } from '@/lib/skillOptions.ts';
import {
  EMPTY_OUTREACH_FILTERS,
  type OutreachFilterState,
} from './outreachFilterState.ts';

const WINDOW_OPTIONS = [
  { value: '6', label: 'Next 6h' },
  { value: '24', label: 'Next 24h' },
  { value: '48', label: 'Next 48h' },
  { value: '168', label: 'Next 7 days' },
  { value: '720', label: 'Next 30 days' },
];

const KIND_OPTIONS: readonly FilterOption<OutreachQueueKind | ''>[] = [
  { value: '', label: 'All kinds' },
  { value: 'recommendation', label: 'Recommendations' },
  { value: 'reminder', label: 'Reminders' },
  { value: 'check_in', label: 'Check-ins' },
];

export interface OutreachFiltersProps {
  /** The header's organisation, which the people offered belong to. */
  organisationId: string | undefined;
  value: OutreachFilterState;
  onChange: (value: OutreachFilterState) => void;
}

/** The filter bar over the queue. */
export function OutreachFilters({
  organisationId,
  value,
  onChange,
}: OutreachFiltersProps) {
  const { data: skills } = useQuery({
    queryKey: ['skills', 'all'],
    queryFn: listAllSkills,
  });

  const skillOptions = useMemo(
    () => [
      { value: '', label: 'All skills' },
      ...(skills ?? []).map((skill) => ({ value: skill.id, label: skill.name })),
    ],
    [skills],
  );

  function set(patch: Partial<OutreachFilterState>) {
    onChange({ ...value, ...patch });
  }

  return (
    <Card tone='quiet'>
      <div className='flex flex-wrap items-center gap-x-5 gap-y-3'>
        {/* The picker has no width of its own; without this it eats the row. */}
        <div className='w-64'>
          <UserPicker
            aria-label='User'
            placeholder='Any user'
            clearable
            organisationId={organisationId}
            value={value.user}
            onChange={(user) => set({ user })}
          />
        </div>
        <FilterSelect
          hideLabel
          label='Skill'
          options={skillOptions}
          value={value.skillId}
          onValueChange={(skillId) => set({ skillId })}
        />
        <FilterSelect
          hideLabel
          label='Kind'
          options={KIND_OPTIONS}
          value={value.kind}
          onValueChange={(kind) => set({ kind })}
        />
        <FilterSelect
          hideLabel
          label='Window'
          options={WINDOW_OPTIONS}
          value={String(value.windowHours)}
          onValueChange={(hours) => set({ windowHours: Number(hours) })}
        />
      </div>

      <div className='mt-3.5 flex items-center justify-between gap-3 border-t border-line pt-3.5'>
        <div className='flex items-center gap-2'>
          {/*
           * Base UI puts the id on its hidden input, so the visible control is
           * the one left unnamed — the aria-label is what a screen reader reads.
           */}
          <Checkbox
            id='include-expired'
            aria-label='Include expired'
            checked={value.includeExpired}
            onCheckedChange={(includeExpired) => set({ includeExpired })}
          />
          <label htmlFor='include-expired' className='text-sm text-ink-secondary'>
            Include expired
          </label>
        </div>
        <Button
          variant='ghost'
          size='sm'
          onClick={() => onChange(EMPTY_OUTREACH_FILTERS)}
        >
          Reset filters
        </Button>
      </div>
    </Card>
  );
}
